import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Film, Star } from "lucide-react";
import axios from "axios";
import YouTubeLite from "@/components/sections/YouTubeLite";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_LABELS = {
  tour: "Property Tour",
  drone: "Drone / Aerial",
  progress: "Build Progress",
  tides: "Tide Timelapse",
  wildlife: "Wildlife",
  general: "General",
};

export default function VideosPage() {
  const [videos, setVideos] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/videos/public`);
        setVideos(res.data.videos || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  // Sort: featured first, then by order
  const sorted = [...videos].sort((a, b) => {
    if (!!b.featured - !!a.featured) return !!b.featured - !!a.featured;
    return (a.order || 0) - (b.order || 0);
  });

  const filtered = filter === "all" ? sorted : sorted.filter(v => v.category === filter);
  const featured = sorted.find(v => v.featured);
  const rest = filtered.filter(v => v.id !== featured?.id);

  // Count videos per category for filter chips
  const categoryCounts = videos.reduce((acc, v) => {
    acc[v.category] = (acc[v.category] || 0) + 1;
    return acc;
  }, {});
  const activeCategories = Object.entries(CATEGORY_LABELS).filter(([k]) => categoryCounts[k] > 0);

  return (
    <>
      {/* Header */}
      <div className="pt-24 pb-8 px-4 sm:px-8 bg-wc-cream">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center gap-3 mb-3">
            <Film className="w-6 h-6 text-wc-teal" />
            <p className="font-accent italic text-wc-teal text-base">Moving pictures</p>
          </div>
          <h1 className="font-display text-wc-forest text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
            Videos
          </h1>
          <p className="font-body text-wc-slate text-base max-w-2xl">
            Drone flyovers, tide timelapses, and quick dispatches from the build. More coming as we bring the site online.
          </p>
        </div>
      </div>

      {/* Category filters */}
      {activeCategories.length > 1 && (
        <div className="px-4 sm:px-8 pb-6 bg-wc-cream">
          <div className="max-w-6xl mx-auto flex flex-wrap gap-2">
            <button onClick={() => setFilter("all")} data-testid="videos-filter-all"
              className={`px-4 py-1.5 rounded-full font-body text-xs transition-all ${
                filter === "all" ? "bg-wc-forest text-wc-cream" : "bg-white border border-wc-teal/30 text-wc-slate hover:text-wc-forest"
              }`}>
              All ({videos.length})
            </button>
            {activeCategories.map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`videos-filter-${k}`}
                className={`px-4 py-1.5 rounded-full font-body text-xs transition-all ${
                  filter === k ? "bg-wc-forest text-wc-cream" : "bg-white border border-wc-teal/30 text-wc-slate hover:text-wc-forest"
                }`}>
                {label} ({categoryCounts[k]})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Featured hero (if exists and in view) */}
      {featured && filter === "all" && (
        <motion.section
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          className="px-4 sm:px-8 pb-10 bg-wc-cream"
          data-testid="videos-featured"
        >
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center gap-2 mb-3">
              <Star className="w-4 h-4 text-wc-teal" />
              <span className="font-body text-wc-teal text-xs uppercase tracking-[0.25em]">Featured</span>
            </div>
            <YouTubeLite
              youtubeId={featured.youtube_id}
              title={featured.title}
              customThumbnailUrl={featured.custom_thumbnail_url}
              aspectClass="aspect-video"
            />
            {featured.description && (
              <p className="font-body text-wc-slate text-sm sm:text-base mt-4 max-w-3xl">
                {featured.description}
              </p>
            )}
          </div>
        </motion.section>
      )}

      {/* Grid */}
      <section className="px-4 sm:px-8 pb-20 bg-white">
        <div className="max-w-6xl mx-auto pt-10">
          {loading ? (
            <p className="text-center font-body text-wc-slate/50 text-sm py-12">Loading…</p>
          ) : rest.length === 0 && !featured ? (
            <div className="text-center py-20">
              <Film className="w-12 h-12 text-wc-slate/30 mx-auto mb-4" />
              <p className="font-body text-wc-slate/60 text-sm">No videos in this category yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {rest.map((v, i) => (
                <motion.div
                  key={v.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: i * 0.05 }}
                  data-testid={`video-card-${v.id}`}
                >
                  <YouTubeLite
                    youtubeId={v.youtube_id}
                    title={v.title}
                    customThumbnailUrl={v.custom_thumbnail_url}
                    aspectClass="aspect-video"
                  />
                  <div className="mt-3">
                    <p className="font-display text-wc-forest text-lg font-semibold leading-tight">{v.title}</p>
                    {v.description && (
                      <p className="font-body text-wc-slate text-sm mt-1 leading-relaxed">{v.description}</p>
                    )}
                    <span className="inline-block mt-2 font-body text-[10px] uppercase tracking-widest text-wc-teal">
                      {CATEGORY_LABELS[v.category] || v.category}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
